import { ROLES, papelDeAcesso } from '../contexts/AuthContext';

/**
 * Configuração completa de navegação do sistema SIDEB
 * Centraliza todas as seções e itens de menu para facilitar manutenção
 *
 * Permissões por role:
 * - Admin: acesso total
 * - Coordenador: tudo exceto Gabaritos e Configurações. Gerencia os Usuários
 *   do próprio município (escopo garantido no backend)
 * - Secretário: enxerga as telas do Coordenador, porém somente leitura — a
 *   escrita é bloqueada no backend. No menu entra como Coordenador (o mesmo
 *   apelido das rotas, `ROLE_ALIASES`), menos onde `deniedRoles` o barra:
 *   RH e Técnico Referência (que a API também nega), Upload e Respostas
 *   Alunos, telas de lançamento
 * - Supervisor: sem Upload e sem Administração
 * - Referência Técnica: leitura completa da cidade (igual Supervisor, sem
 *   Administração e sem criação no módulo de Avaliações)
 * - Professor: leitura/escrita apenas das próprias turmas/alunos/diário
 *   (sem Administração, sem Sistema, sem Provas/Upload/Respostas)
 * - Gestor: apenas Dashboard, Relatórios e IDEB
 */
export const allNavSections = [
  {
    title: 'Principal',
    allowedRoles: [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.SUPERVISOR, ROLES.GESTOR, ROLES.REFERENCIA_TECNICA, ROLES.PROFESSOR],
    items: [
      // `fixo`: não pode ser escondido em Acessos por Usuário. O Dashboard é
      // para onde o login e as rotas barradas mandam a pessoa.
      { icon: '📊', label: 'Dashboard', href: '/dashboard', fixo: true },
      { icon: '📈', label: 'Relatórios', href: '/relatorios' },
      { icon: '🎯', label: 'IDEB', href: '/ideb-municipal', allowedRoles: [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.SUPERVISOR, ROLES.GESTOR, ROLES.REFERENCIA_TECNICA] },
      { icon: '💰', label: 'Simulador Financeiro', href: '/simulador-financeiro', allowedRoles: [ROLES.ADMIN] },
    ]
  },
  {
    title: 'Gestão Escolar',
    allowedRoles: [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.SUPERVISOR, ROLES.REFERENCIA_TECNICA, ROLES.PROFESSOR],
    items: [
      { icon: '🏫', label: 'Escolas', href: '/escolas', allowedRoles: [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.SUPERVISOR, ROLES.REFERENCIA_TECNICA] },
      { icon: '👥', label: 'Turmas', href: '/turmas' },
      { icon: '👨‍🏫', label: 'Professores', href: '/professores', activeMatch: /^\/professores(\/|$)/, allowedRoles: [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.SUPERVISOR, ROLES.REFERENCIA_TECNICA] },
      { icon: '👤', label: 'Alunos', href: '/alunos' },
      { icon: '🧩', label: 'Alunos AEE', href: '/alunos-tea' },
    ]
  },
  {
    title: 'Diário',
    allowedRoles: [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.SUPERVISOR, ROLES.PROFESSOR, ROLES.REFERENCIA_TECNICA],
    items: [
      { icon: '🗺️', label: 'Painel da Rede', href: '/dashboard?aba=diario', description: 'Frequência, notas e aulas — aba Diário do Dashboard', allowedRoles: [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.SUPERVISOR, ROLES.REFERENCIA_TECNICA] },
      { icon: '📗', label: 'Meu Painel', href: '/meu-painel', description: 'Sua turma: pendências, frequência e notas', allowedRoles: [ROLES.PROFESSOR, ROLES.ADMIN] },
      // Atalhos diretos: as rotas /diario e /grade-horaria escolhem a turma
      // sozinhas (e pulam a escolha quando o professor só tem uma).
      // `activeMatch` mantém o item aceso quando a navegação já entrou numa
      // turma, onde a URL não é mais o href do atalho.
      { icon: '📖', label: 'Diário de Turma', href: '/diario', description: 'Registro de aulas, frequência e notas', activeMatch: /^\/(diario|turmas\/[^/]+\/diario)/ },
      { icon: '🕘', label: 'Grade Horária', href: '/grade-horaria', description: 'Quadro de aulas da turma — e a porta para a chamada', activeMatch: /^\/(grade-horaria|turmas\/[^/]+\/grade-horaria)/ },
      { icon: '🔎', label: 'Acompanhamento de Diários', href: '/diarios/acompanhamento', description: 'Aulas previstas x lançadas, dia a dia' },
      { icon: '🏖️', label: 'Calendário Letivo', href: '/diarios/calendario-letivo', description: 'Feriados e dias sem aula' },
    ]
  },
  {
    title: 'Avaliações',
    allowedRoles: [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.SUPERVISOR, ROLES.GESTOR, ROLES.REFERENCIA_TECNICA, ROLES.PROFESSOR],
    items: [
      { icon: '📝', label: 'Provas', href: '/provas', allowedRoles: [ROLES.ADMIN, ROLES.COORDENADOR] },
      { icon: '📤', label: 'Upload', href: '/inputs', activeMatch: /^\/(inputs|upload)\/?$/, allowedRoles: [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA], deniedRoles: [ROLES.SECRETARIO] },
      { icon: '✍️', label: 'Respostas Alunos', href: '/respostas-alunos', allowedRoles: [ROLES.ADMIN, ROLES.COORDENADOR], deniedRoles: [ROLES.SECRETARIO] },
      { icon: '🎯', label: 'Descritores', href: '/descritores' },
    ]
  },
  {
    title: 'Administração',
    // RT vê esta seção: Atribuição (leitura) e RH — Funcionários.
    allowedRoles: [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA],
    items: [
      { icon: '🔗', label: 'Técnico Referência do Município', href: '/gerentes-escolas', allowedRoles: [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA], deniedRoles: [ROLES.SECRETARIO] },
      { icon: '🧑‍💼', label: 'RH — Funcionários', href: '/funcionarios', allowedRoles: [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA], deniedRoles: [ROLES.SECRETARIO] },
      { icon: '🗓️', label: 'Frequência RH', href: '/funcionarios/frequencia', allowedRoles: [ROLES.ADMIN, ROLES.COORDENADOR], deniedRoles: [ROLES.SECRETARIO] },
    ]
  },
  {
    title: 'Sistema',
    allowedRoles: [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.SECRETARIO, ROLES.REFERENCIA_TECNICA],
    items: [
      { icon: '📋', label: 'Gabaritos Prova', href: '/gabaritos', allowedRoles: [ROLES.ADMIN] },
      { icon: '👨‍💼', label: 'Usuários', href: '/usuarios', activeMatch: /^\/usuarios(\/|$)/, allowedRoles: [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.SECRETARIO, ROLES.REFERENCIA_TECNICA] },
      { icon: '⚙️', label: 'Configurações', href: '/configuracoes', activeMatch: /^\/configuracoes(\/|$)/, allowedRoles: [ROLES.ADMIN] },
    ]
  }
];

/**
 * Seções e itens que o PERFIL permite, sem o ajuste por usuário.
 * É a lista que Configurações > Acessos por Usuário oferece para esconder.
 * @param {string} role - Papel do usuário
 * @returns {Array} Seções de navegação do perfil
 */
export const getNavSectionsDoPerfil = (role) => {
  // O Secretário entra como Coordenador, como nas rotas (`hasAnyRole`). Só
  // aqui: `papelDeAcesso` não o converte porque `hasRole(COORDENADOR)` libera
  // escrita nas telas.
  const userRole = role === ROLES.SECRETARIO ? ROLES.COORDENADOR : papelDeAcesso(role);
  const permitido = (alvo) => alvo.allowedRoles.includes(userRole)
    && !(alvo.deniedRoles || []).includes(role);

  return allNavSections
    .filter(permitido)
    .map(section => ({
      ...section,
      items: section.items.filter(item => {
        // Se o item tem allowedRoles específico, usa ele
        // Caso contrário, herda da seção (todos os itens da seção são permitidos)
        if (item.allowedRoles) {
          return permitido(item);
        }
        return !(item.deniedRoles || []).includes(role);
      })
    }))
    .filter(section => section.items.length > 0); // Remove seções vazias
};

/**
 * `href`s escondidos desta conta em Acessos por Usuário. O administrador
 * não tem: o ajuste só restringe, e ninguém tranca quem destranca os outros.
 */
export const abasOcultasDo = (user) => {
  if (!user || user.role === ROLES.ADMIN) return [];
  return Array.isArray(user.abas_ocultas) ? user.abas_ocultas : [];
};

/** A aba `href` foi escondida desta conta? */
export const abaOculta = (user, href) => abasOcultasDo(user).includes(href);

/**
 * Filtra as seções de navegação baseado no role do usuário e nas abas
 * escondidas da conta.
 * @param {Object} user - Objeto do usuário atual
 * @returns {Array} Seções de navegação filtradas
 */
export const getNavSections = (user) => {
  if (!user) return [];

  const ocultas = abasOcultasDo(user);

  return getNavSectionsDoPerfil(user.role)
    .map(section => ({
      ...section,
      items: section.items.filter(item => item.fixo || !ocultas.includes(item.href)),
    }))
    .filter(section => section.items.length > 0);
};

/**
 * A rota `pathname` pertence a uma aba escondida desta conta?
 *
 * Esconder do menu não basta: o endereço continua no histórico e nos
 * favoritos. Casa pelo `activeMatch` do item (que cobre as telas de dentro,
 * como /turmas/12/diario no Diário de Turma) ou pelo `href` exato. Itens com
 * `?` no `href` (Painel da Rede, uma aba do Dashboard) não barram rota: a
 * própria tela esconde a aba.
 */
export const rotaOculta = (user, pathname) => {
  const ocultas = abasOcultasDo(user);
  if (!ocultas.length) return false;

  return allNavSections.some(section => section.items.some(item => {
    if (item.fixo || !ocultas.includes(item.href)) return false;
    if (item.activeMatch) return item.activeMatch.test(pathname);
    return !item.href.includes('?') && item.href === pathname;
  }));
};

/**
 * Marca o item ativo baseado na rota atual
 * @param {Array} sections - Seções de navegação
 * @param {string} currentPath - Caminho atual (ex: '/dashboard')
 * @returns {Array} Seções com item ativo marcado
 */
export const markActiveItem = (sections, currentPath) => {
  return sections.map(section => ({
    ...section,
    items: section.items.map(item => ({
      ...item,
      // Quando o item define `activeMatch` (regex), ele só é marcado como
      // ativo se a rota atual casar com esse padrão. Isso evita que dois
      // itens que apontam para o mesmo `href` (ex.: "Turmas" e "Diário de
      // Turma", ambos em /turmas) fiquem destacados ao mesmo tempo:
      // "Diário de Turma" só acende em /turmas/:id/diario.
      active: item.activeMatch
        ? item.activeMatch.test(currentPath)
        : item.href === currentPath,
    })),
  }));
};
